import { Alert, Box, Button, Chip, CircularProgress, IconButton, LinearProgress, Paper, Popper, Stack, Typography, alpha, useTheme } from "@mui/material";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RefreshIcon from "@mui/icons-material/Refresh";
import { format, formatDistanceToNow } from "date-fns";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import {
    isActiveProcessingJob,
    isFailedProcessingJob,
    isSuccessfulProcessingJob,
    normalizeProcessingJobStatus,
    type ProcessingJob,
    type ProcessingJobActivity
} from "./types";
import { useBackgroundActivity } from "./useBackgroundActivity";
import { BatchGenerationBackgroundRow } from "../batchGeneration";

export const BACKGROUND_ACTIVITY_FLYOUT_ID = "background-activity-flyout";

interface BackgroundActivityFlyoutProps {
    anchorEl: HTMLElement | null;
}

const titleFor = (job: ProcessingJob): string => {
    if (job.kind === "work_product") return job.workProductName || "Work Product generation";
    return job.productName || "Product build";
};

const statusDetails = (job: Pick<ProcessingJob, "status">) => {
    const status = normalizeProcessingJobStatus(job.status);
    if (isSuccessfulProcessingJob(job)) return { label: "Completed", color: "success" as const };
    if (isFailedProcessingJob(job)) return { label: status.startsWith("cancel") ? "Cancelled" : "Failed", color: "error" as const };
    if (status === "skipped") return { label: "Skipped", color: "default" as const };
    if (status === "queued" || status === "pending" || status === "not_started") {
        return { label: "Queued", color: "default" as const };
    }
    return { label: "In progress", color: "primary" as const };
};

const currentActivityFor = (job: ProcessingJob): ProcessingJobActivity | null => {
    if (job.kind !== "product" || !job.activities?.length) return null;
    return (
        job.activities.find(activity => activity.id === job.currentActivityId) ??
        job.activities.find(activity => ["running", "processing", "in_progress", "started"].includes(normalizeProcessingJobStatus(activity.status))) ??
        job.activities.find(activity => ["not_started", "queued", "pending"].includes(normalizeProcessingJobStatus(activity.status))) ??
        job.activities.find(activity => isFailedProcessingJob(activity)) ??
        job.activities[job.activities.length - 1]
    );
};

interface TimeDetails {
    relative: string;
    iso: string;
    exact: string;
}

const safeTimeDetails = (job: ProcessingJob): TimeDetails | null => {
    const successful = isSuccessfulProcessingJob(job);
    const failed = isFailedProcessingJob(job);
    const value = successful || failed ? job.completedAt || job.updatedAt : job.startedAt || job.createdAt || job.updatedAt;
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    const prefix = successful || failed ? "Finished" : job.startedAt ? "Started" : "Queued";
    return {
        relative: `${prefix} ${formatDistanceToNow(date, { addSuffix: true })}`,
        iso: date.toISOString(),
        exact: format(date, "PPpp")
    };
};

const sortJobs = (jobs: ProcessingJob[]): ProcessingJob[] =>
    [...jobs].sort((left, right) => {
        const activityDifference = Number(isActiveProcessingJob(right)) - Number(isActiveProcessingJob(left));
        if (activityDifference) return activityDifference;
        const leftTime = Date.parse(left.updatedAt || left.startedAt || left.createdAt || "") || 0;
        const rightTime = Date.parse(right.updatedAt || right.startedAt || right.createdAt || "") || 0;
        return rightTime - leftTime;
    });

function ActivityRow({
    job,
    focused,
    retrying,
    retryError,
    onRetry
}: {
    job: ProcessingJob;
    focused: boolean;
    retrying: boolean;
    retryError: string | null;
    onRetry: () => void;
}) {
    const theme = useTheme();
    const rowRef = useRef<HTMLLIElement>(null);
    const disclosureId = useId();
    const [expanded, setExpanded] = useState(false);
    const active = isActiveProcessingJob(job);
    const successful = isSuccessfulProcessingJob(job);
    const failed = isFailedProcessingJob(job);
    const status = statusDetails(job);
    const time = safeTimeDetails(job);
    const EntityIcon = job.kind === "work_product" ? DescriptionOutlinedIcon : Inventory2OutlinedIcon;
    const activities = job.kind === "product" ? (job.activities ?? []) : [];
    const currentActivity = currentActivityFor(job);
    const failedActivity = job.kind === "product" ? activities.find(isFailedProcessingJob) : null;
    const displayMessage = currentActivity?.message || (job.message !== currentActivity?.label ? job.message : null);

    useEffect(() => {
        if (focused && typeof rowRef.current?.scrollIntoView === "function") {
            rowRef.current.scrollIntoView({ block: "nearest" });
        }
    }, [focused]);

    return (
        <Stack
            ref={rowRef}
            component="li"
            direction="row"
            spacing={1.5}
            sx={{
                listStyle: "none",
                px: 2,
                py: 1.6,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.72)}`,
                bgcolor: focused ? alpha(theme.palette.primary.main, 0.09) : "transparent",
                boxShadow: focused ? `inset 3px 0 0 ${theme.palette.primary.main}` : "none",
                transition: "background-color 160ms ease"
            }}
            data-focused={focused ? "true" : undefined}
        >
            <Box
                sx={{
                    width: 34,
                    height: 34,
                    flex: "0 0 auto",
                    borderRadius: 1.5,
                    display: "grid",
                    placeItems: "center",
                    color: successful ? "success.main" : failed ? "error.main" : "primary.main",
                    bgcolor: successful
                        ? alpha(theme.palette.success.main, 0.1)
                        : failed
                          ? alpha(theme.palette.error.main, 0.1)
                          : alpha(theme.palette.primary.main, 0.09)
                }}
            >
                {successful ? (
                    <CheckCircleOutlineIcon sx={{ fontSize: 19 }} />
                ) : failed ? (
                    <ErrorOutlineIcon sx={{ fontSize: 19 }} />
                ) : (
                    <EntityIcon sx={{ fontSize: 18 }} />
                )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="flex-start" spacing={1}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, lineHeight: 1.35, fontWeight: 700 }} noWrap title={titleFor(job)}>
                            {titleFor(job)}
                        </Typography>
                        <Typography sx={{ mt: 0.15, fontSize: 11.25, color: "text.secondary" }} noWrap>
                            {job.kind === "work_product" ? `Work Product${job.productName ? ` · ${job.productName}` : ""}` : "Product"}
                        </Typography>
                    </Box>
                    <Chip
                        label={status.label}
                        color={status.color}
                        size="small"
                        variant={status.color === "default" ? "outlined" : "filled"}
                        sx={{ height: 22, fontSize: 10.5, fontWeight: 700, "& .MuiChip-label": { px: 0.9 } }}
                    />
                    {activities.length > 0 && (
                        <IconButton
                            size="small"
                            aria-label={`${expanded ? "Collapse" : "Expand"} activities for ${titleFor(job)}`}
                            aria-expanded={expanded}
                            aria-controls={disclosureId}
                            onClick={() => setExpanded(value => !value)}
                            sx={{ width: 24, height: 24, ml: -0.25 }}
                        >
                            <ExpandMoreIcon
                                sx={{
                                    fontSize: 18,
                                    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                                    transition: "transform 160ms ease"
                                }}
                            />
                        </IconButton>
                    )}
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mt: 0.65 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        {currentActivity && (
                            <Typography sx={{ fontSize: 11.5, lineHeight: 1.4, fontWeight: 650 }}>{currentActivity.label}</Typography>
                        )}

                        {displayMessage && (
                            <Typography sx={{ mt: currentActivity ? 0.15 : 0, fontSize: 11.5, lineHeight: 1.4, color: failed ? "error.main" : "text.secondary" }}>
                                {displayMessage}
                            </Typography>
                        )}
                    </Box>

                    {failedActivity && (
                        <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={retrying}
                            onClick={onRetry}
                            sx={{ px: 1.15, minWidth: 0, fontSize: 11, fontWeight: 700, textTransform: "none" }}
                        >
                            {retrying ? "Retrying…" : "Retry"}
                        </Button>
                    )}
                </Stack>

                {retryError && (
                    <Typography role="alert" sx={{ mt: 0.65, fontSize: 11, lineHeight: 1.4, color: "error.main" }}>
                        {retryError}
                    </Typography>
                )}

                {active && (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                        <LinearProgress
                            variant={job.progress === null ? "indeterminate" : "determinate"}
                            value={job.progress ?? undefined}
                            aria-label={`${titleFor(job)} progress`}
                            sx={{ flex: 1, height: 4, borderRadius: 4 }}
                        />
                        {job.progress !== null && (
                            <Typography sx={{ minWidth: 30, fontSize: 10.5, fontWeight: 700, color: "text.secondary", textAlign: "right" }}>
                                {job.progress}%
                            </Typography>
                        )}
                    </Stack>
                )}

                {time && (
                    <Stack direction="row" alignItems="center" spacing={0.45} sx={{ mt: active ? 0.75 : 0.8, color: "text.secondary" }}>
                        <AccessTimeOutlinedIcon sx={{ fontSize: 12.5 }} />
                        <Typography component="time" dateTime={time.iso} title={time.exact} sx={{ fontSize: 10.75 }}>
                            {time.relative}
                        </Typography>
                    </Stack>
                )}

                {expanded && activities.length > 0 && (
                    <Box
                        id={disclosureId}
                        role="region"
                        aria-label={`${titleFor(job)} activity details`}
                        sx={{ mt: 1.25, pt: 1.1, borderTop: `1px solid ${alpha(theme.palette.divider, 0.72)}` }}
                    >
                        <Stack component="ul" aria-label={`${titleFor(job)} activities`} spacing={1.15} sx={{ p: 0, m: 0 }}>
                            {activities.map(activity => {
                                const activityStatus = statusDetails(activity);
                                const activitySuccessful = isSuccessfulProcessingJob(activity);
                                const activityFailed = isFailedProcessingJob(activity);
                                const isCurrent = activity.id === currentActivity?.id;
                                const activityProgress = activity.progress ?? (isCurrent ? job.progress : null);
                                return (
                                    <Stack
                                        key={activity.id}
                                        component="li"
                                        direction="row"
                                        alignItems="flex-start"
                                        spacing={0.9}
                                        aria-current={isCurrent ? "step" : undefined}
                                        sx={{ listStyle: "none" }}
                                    >
                                        <Box
                                            aria-hidden="true"
                                            sx={{
                                                width: 17,
                                                height: 17,
                                                mt: 0.1,
                                                flex: "0 0 auto",
                                                display: "grid",
                                                placeItems: "center",
                                                color: activitySuccessful
                                                    ? "success.main"
                                                    : activityFailed
                                                      ? "error.main"
                                                      : activityStatus.color === "primary"
                                                        ? "primary.main"
                                                        : "text.disabled"
                                            }}
                                        >
                                            {activitySuccessful ? (
                                                <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                                            ) : activityFailed ? (
                                                <ErrorOutlineIcon sx={{ fontSize: 16 }} />
                                            ) : activityStatus.color === "primary" ? (
                                                <CircularProgress size={13} thickness={5} />
                                            ) : (
                                                <RadioButtonUncheckedIcon sx={{ fontSize: 14 }} />
                                            )}
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
                                                <Typography sx={{ fontSize: 11.5, lineHeight: 1.35, fontWeight: isCurrent ? 700 : 600 }}>
                                                    {activity.label}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        flex: "0 0 auto",
                                                        fontSize: 10.25,
                                                        fontWeight: 650,
                                                        color: activityFailed
                                                            ? "error.main"
                                                            : activitySuccessful
                                                              ? "success.main"
                                                              : activityStatus.color === "primary"
                                                                ? "primary.main"
                                                                : "text.secondary"
                                                    }}
                                                >
                                                    {activityStatus.label}
                                                </Typography>
                                            </Stack>
                                            {activity.message && (
                                                <Typography sx={{ mt: 0.15, fontSize: 10.75, lineHeight: 1.4, color: activityFailed ? "error.main" : "text.secondary" }}>
                                                    {activity.message}
                                                </Typography>
                                            )}
                                            {activityProgress !== null && (
                                                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.55 }}>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={activityProgress}
                                                        aria-label={`${activity.label} progress`}
                                                        sx={{ flex: 1, height: 3, borderRadius: 3 }}
                                                    />
                                                    <Typography sx={{ minWidth: 27, fontSize: 9.75, fontWeight: 650, color: "text.secondary", textAlign: "right" }}>
                                                        {activityProgress}%
                                                    </Typography>
                                                </Stack>
                                            )}
                                        </Box>
                                    </Stack>
                                );
                            })}
                        </Stack>
                    </Box>
                )}
            </Box>
        </Stack>
    );
}

export function BackgroundActivityFlyout({ anchorEl }: BackgroundActivityFlyoutProps) {
    const theme = useTheme();
    const { jobs, batches, runningCount, queuedCount, finishedCount, isLoading, isRefreshing, isClearing, retryingJobId, retryErrors, error, isOpen, focusedJobId, close, refresh, clearFinished, retryFailedJob } =
        useBackgroundActivity();
    const sortedJobs = useMemo(() => sortJobs(jobs), [jobs]);
    const sortedBatches = useMemo(
        () => [...batches].sort((left, right) => Date.parse(right.handle?.updatedAt || right.createdAt) - Date.parse(left.handle?.updatedAt || left.createdAt)),
        [batches]
    );
    const hasFinishedBatch = batches.some(batch => ["completed", "partial_failure", "failed", "cancelled", "blocked"].includes(batch.phase));
    const hasActivity = sortedJobs.length > 0 || sortedBatches.length > 0;

    return (
        <Popper
            open={isOpen && Boolean(anchorEl)}
            anchorEl={anchorEl}
            placement="right-end"
            modifiers={[
                { name: "offset", options: { offset: [0, 10] } },
                { name: "flip", enabled: true },
                { name: "preventOverflow", options: { padding: 12 } }
            ]}
            sx={{ zIndex: theme.zIndex.drawer + 2 }}
        >
            <Paper
                elevation={0}
                sx={{
                    width: "min(460px, calc(100vw - 24px))",
                    maxWidth: "calc(100vw - 24px)",
                    maxHeight: "min(720px, calc(100vh - 24px))",
                    overflow: "hidden",
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2.5,
                    boxShadow: "0 18px 60px rgba(15, 23, 42, 0.24)"
                }}
            >
                <Box
                    id={BACKGROUND_ACTIVITY_FLYOUT_ID}
                    role="dialog"
                    aria-modal="false"
                    aria-labelledby="background-activity-title"
                    sx={{ display: "flex", flexDirection: "column", maxHeight: "min(720px, calc(100vh - 24px))" }}
                >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.6, borderBottom: `1px solid ${theme.palette.divider}` }}>
                        <PendingActionsOutlinedIcon sx={{ fontSize: 21, color: "primary.main" }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography id="background-activity-title" sx={{ fontSize: 14, fontWeight: 750 }}>
                                Background activity
                            </Typography>
                            <Typography aria-live="polite" sx={{ fontSize: 11.25, color: "text.secondary" }}>
                                {runningCount} running · {queuedCount} queued
                            </Typography>
                        </Box>
                        <IconButton size="small" aria-label="Close background activity" onClick={close}>
                            <CloseIcon sx={{ fontSize: 19 }} />
                        </IconButton>
                    </Stack>

                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ px: 2, py: 1, bgcolor: alpha(theme.palette.primary.main, 0.025), borderBottom: `1px solid ${theme.palette.divider}` }}
                    >
                        <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>Updates every 5 seconds</Typography>
                        {(finishedCount > 0 || hasFinishedBatch) && (
                            <Button
                                size="small"
                                color="inherit"
                                disabled={isClearing}
                                onClick={() => void clearFinished()}
                                sx={{ minWidth: 0, px: 1, fontSize: 11.5, fontWeight: 650, textTransform: "none" }}
                            >
                                {isClearing ? "Clearing…" : "Clear finished"}
                            </Button>
                        )}
                    </Stack>

                    <Box sx={{ overflowY: "auto", minHeight: 150, maxHeight: 360 }}>
                        {isLoading && !hasActivity ? (
                            <Stack alignItems="center" justifyContent="center" spacing={1.2} sx={{ minHeight: 210, p: 3 }} role="status">
                                <CircularProgress size={28} />
                                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Loading background activity…</Typography>
                            </Stack>
                        ) : error && !hasActivity ? (
                            <Stack alignItems="center" justifyContent="center" spacing={1.25} sx={{ minHeight: 210, p: 3, textAlign: "center" }}>
                                <ErrorOutlineIcon color="error" sx={{ fontSize: 28 }} />
                                <Typography sx={{ fontSize: 12.5, fontWeight: 650 }}>Activity couldn’t be loaded</Typography>
                                <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{error}</Typography>
                                <Button
                                    size="small"
                                    startIcon={<RefreshIcon />}
                                    disabled={isRefreshing}
                                    onClick={() => void refresh()}
                                    sx={{ textTransform: "none" }}
                                >
                                    Retry
                                </Button>
                            </Stack>
                        ) : !hasActivity ? (
                            <Stack alignItems="center" justifyContent="center" spacing={0.8} sx={{ minHeight: 210, p: 3, textAlign: "center" }}>
                                <PendingActionsOutlinedIcon sx={{ fontSize: 30, color: "text.disabled" }} />
                                <Typography sx={{ fontSize: 12.5, fontWeight: 650 }}>No background activity</Typography>
                                <Typography sx={{ maxWidth: 300, fontSize: 11.5, color: "text.secondary" }}>
                                    Product builds, Work Product jobs, and batch generation will appear here.
                                </Typography>
                            </Stack>
                        ) : (
                            <Box component="ul" aria-label="Background activities" sx={{ p: 0, m: 0 }}>
                                {error && (
                                    <Box component="li" sx={{ listStyle: "none", px: 1.5, pt: 1.25 }}>
                                        <Alert
                                            severity="warning"
                                            action={
                                                <Button color="inherit" size="small" disabled={isRefreshing} onClick={() => void refresh()}>
                                                    Retry
                                                </Button>
                                            }
                                            sx={{ fontSize: 11.5 }}
                                        >
                                            Updates are temporarily unavailable. Showing the latest activity.
                                        </Alert>
                                    </Box>
                                )}
                                {sortedBatches.map(batch => (
                                    <BatchGenerationBackgroundRow key={batch.trackingId} batch={batch} />
                                ))}
                                {sortedJobs.map(job => (
                                    <ActivityRow
                                        key={job.id}
                                        job={job}
                                        focused={job.id === focusedJobId}
                                        retrying={job.id === retryingJobId}
                                        retryError={retryErrors[job.id] ?? null}
                                        onRetry={() => void retryFailedJob(job.id)}
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Paper>
        </Popper>
    );
}
